import { PriceLabel, PriceLabelSimple, renderDatetimeInline } from '@/components/features/shared/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SelectableRow } from '@/components/ui/selectable-row'
import { BillingType } from '@/domain/invoices/invoice'
import { PaymentStatus } from '@/domain/payments/payments'
import { AlertCircle, ArrowLeft, CheckSquare, FileText, Receipt, ReceiptText, X } from 'lucide-react'
import { useState } from 'react'
import { PaymentRow } from '../sub-components/PaymentRow'

interface SelectCompletedPaymentsForInvoicingModalProps {
  isOpen: boolean
  onClose: () => void
  payments: PaymentRow[]
  onInvoice: (paymentIds: string[], billingType: BillingType) => void
  isPending: boolean
}

export const SelectCompletedPaymentsForInvoicingModal = ({
  isOpen,
  onClose,
  payments,
  onInvoice,
  isPending,
}: SelectCompletedPaymentsForInvoicingModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [billingType, setBillingType] = useState<BillingType>(BillingType.Invoice)
  const [step, setStep] = useState<'select' | 'confirm'>('select')

  const completedPayments = payments.filter(p => p.status === PaymentStatus.COMPLETED || p.status === PaymentStatus.IN_COLLECTOR)

  const totalSelected = selectedIds.size
  const totalAmount = completedPayments
    .filter(p => selectedIds.has(p.id))
    .reduce((sum, p) => sum + (p.amount || 0), 0)

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === completedPayments.length) {
        return new Set()
      }
      return new Set(completedPayments.map(p => p.id))
    })
  }

  const handleConfirm = () => {
    if (selectedIds.size === 0) return
    setStep('confirm')
  }

  const handleFinalConfirm = () => {
    if (selectedIds.size === 0) return
    onInvoice(Array.from(selectedIds), billingType)
    onClose()
    setSelectedIds(new Set())
    setStep('select')
  }

  const handleBack = () => {
    setStep('select')
  }

  if (completedPayments.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Facturar pagos</DialogTitle>
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
              <AlertCircle className="h-4 w-4" />
              No hay pagos completados para facturar
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Facturar pagos completados</DialogTitle>
          <DialogDescription>
            Elige los pagos a facturar que ya se encuentren abonados.
          </DialogDescription>
        </DialogHeader>

        {step === 'select' && (
          <>
            <div className="space-y-3 my-4">
              <div className="flex items-center justify-between pb-2 border-b">
                <span className="text-sm text-muted-foreground font-medium">
                  {completedPayments.length} pago {completedPayments.length !== 1 ? 's' : ''} completado{completedPayments.length !== 1 ? 's' : ''}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={selectAll}
                  disabled={isPending}
                >
                  <CheckSquare className="h-3 w-3 mr-1" />
                  Seleccionar todos
                </Button>
              </div>

              <div className="flex items-center gap-4 pb-2">
                <span className="text-sm font-medium">Tipo de documento:</span>
                <Select value={billingType} onValueChange={(v) => setBillingType(v as BillingType)} disabled={isPending}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={BillingType.Invoice}>Factura (AFIP)</SelectItem>
                    <SelectItem value={BillingType.Receipt}>Recibo interno</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 overflow-y-auto pr-2 flex-grow">
                {completedPayments.map(p => {
                  const isSelected = selectedIds.has(p.id)
                  return (
                    <SelectableRow
                      key={p.id}
                      checked={isSelected}
                      disabled={isPending}
                      onCheckChange={() => toggleSelection(p.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isSelected}
                          disabled={isPending}
                        />
                        <div>
                          <div className="mt-1 inline-block text-sm">
                            <Label className='ml-0'>Rubro: </Label>
                            <span className='font-medium'> {p.__context.offeringCode} - {p.__context.offeringName} </span>
                          </div>
                          <div className="text-sm">
                            <Label className='ml-0'>Concepto: </Label>
                            <span className='font-mono font-bold'>{p.__context.label}</span>
                          </div>
                          <div className="text-sm flex items-center gap-1">
                            <Label className='ml-0'>Fecha de pago: </Label>
                            <span className=''>{renderDatetimeInline(p.paymentDate)}</span>
                          </div>
                        </div>
                      </div>
                      <PriceLabel price={(p.amount || 0)} />
                    </SelectableRow>
                  )
                })}
              </div>
            </div>

            <div className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
              <span className="text-sm font-medium">Total a facturar:</span>
              <span className="text-lg font-bold font-mono">${totalAmount.toLocaleString('es-AR')}</span>
            </div>

            <DialogFooter className="mt-4">
              <div className='flex justify-between w-full'>
                <Button variant="outline" onClick={onClose} disabled={isPending}>
                  <X className="h-3.5 w-3.5 mr-1.5" />
                  Cancelar
                </Button>
                <Button
                  variant="warning"
                  onClick={handleConfirm}
                  disabled={selectedIds.size === 0 || isPending}
                >
                  <Receipt className="h-3.5 w-3.5 mr-1.5" />
                  Continuar ({totalSelected})
                </Button>
              </div>
            </DialogFooter>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="space-y-4 my-4">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                Revisá tu selección antes de confirmar
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tipo de documento:</span>
                  <span className="font-medium flex items-center gap-1.5">
                    {billingType === BillingType.Invoice ? (
                      <><FileText className="h-3.5 w-3.5" /> Factura (AFIP)</>
                    ) : (
                      <><ReceiptText className="h-3.5 w-3.5" /> Recibo interno</>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Pagos seleccionados:</span>
                  <span className="font-medium">{totalSelected}</span>
                </div>
              </div>

              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {completedPayments
                  .filter(p => selectedIds.has(p.id))
                  .map(p => (
                    <div key={p.id} className="flex items-center justify-between py-2 px-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.__context.offeringCode} - {p.__context.offeringName}</div>
                        <div className="text-muted-foreground text-xs">{p.__context.label}</div>
                      </div>
                      <PriceLabel price={(p.amount || 0)} />
                    </div>
                  ))}
              </div>

              <div className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
                <span className="text-sm font-medium">Total a facturar:</span>
                <span className="text-lg font-bold font-mono">${totalAmount.toLocaleString('es-AR')}</span>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <div className='flex justify-between w-full'>
                <Button variant="outline" onClick={handleBack} disabled={isPending}>
                  <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                  Volver
                </Button>
                <Button
                  variant="warning"
                  onClick={handleFinalConfirm}
                  disabled={selectedIds.size === 0 || isPending}
                >
                  {billingType === BillingType.Invoice ? <FileText className="h-4 w-4" /> : <ReceiptText className="h-4 w-4" />}
                  {billingType === BillingType.Invoice ? "Emitir factura" : "Emitir recibo"} por <PriceLabelSimple price={totalAmount} />
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
